import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import type { PortfolioSummary } from "../../utils/portfolioIntelligenceUtils";

interface PortfolioSummaryCardsProps {
  summary: PortfolioSummary;
  loading: boolean;
}

const PortfolioSummaryCards: React.FC<PortfolioSummaryCardsProps> = ({
  summary,
  loading,
}) => {
  const reuseGap = Math.max(0, summary.reuseTargetPercent - summary.reuseRatePercent);

  return (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      <Grid item xs={12} md={4}>
        <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: 700 }}
            >
              TOTAL APIS
            </Typography>
            {loading ? (
              <Skeleton width={80} height={48} sx={{ mt: 1 }} />
            ) : (
              <Typography variant="h3" fontWeight={700} sx={{ mt: 0.5 }}>
                {summary.totalApis}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "warning.main" }} />
              {loading ? (
                <Skeleton width={120} />
              ) : (
                <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600 }}>
                  {summary.ungovernedPercent}% ungoverned
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: 700 }}
            >
              DUPLICATE RISK
            </Typography>
            {loading ? (
              <Skeleton width={60} height={48} sx={{ mt: 1 }} />
            ) : (
              <Typography variant="h3" fontWeight={700} sx={{ mt: 0.5, color: "error.main" }}>
                {summary.duplicateRiskCount}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
              <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: "error.main" }} />
              {loading ? (
                <Skeleton width={160} />
              ) : (
                <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
                  APIs with ≥70% overlap
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: 700 }}
            >
              REUSE RATE
            </Typography>
            {loading ? (
              <Skeleton width={80} height={48} sx={{ mt: 1 }} />
            ) : (
              <Typography variant="h3" fontWeight={700} sx={{ mt: 0.5, color: "warning.main" }}>
                {summary.reuseRatePercent}%
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
              <TrendingDownOutlinedIcon sx={{ fontSize: 16, color: "warning.main" }} />
              {loading ? (
                <Skeleton width={180} />
              ) : (
                <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600 }}>
                  Target: {summary.reuseTargetPercent}% · gap of {reuseGap}pts
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PortfolioSummaryCards;
